import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiTrash2, FiEdit2, FiCopy, FiRefreshCw, FiZap } from 'react-icons/fi';

const authH = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const iconBtn = (bg, color) => ({
  background: bg, border: 'none', borderRadius: 6, padding: '5px 7px',
  cursor: 'pointer', color, display: 'flex', alignItems: 'center'
});

export default function AdminMessageTemplates({ token }) {
  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sample, setSample]   = useState('');
  const [count, setCount]     = useState(5);
  const [label, setLabel]     = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/message-templates', authH(token));
      setTemplates(r.data.templates || []);
    } catch(e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!sample.trim()) return toast.warn('Paste a sample template first');
    setGenerating(true);
    try {
      const r = await axios.post('/api/admin/message-templates/generate', { sample, count, label }, authH(token));
      setTemplates(prev => [...(r.data.templates || []), ...prev]);
      toast.success(`✅ ${r.data.templates?.length} templates generated and saved`);
      setSample(''); setLabel('');
    } catch(e) { toast.error(e.response?.data?.error || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`/api/admin/message-templates/${id}`, authH(token));
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Deleted');
    } catch(e) { toast.error('Delete failed'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.patch(`/api/admin/message-templates/${editing.id}`, { name: editing.name, content: editing.content }, authH(token));
      setTemplates(prev => prev.map(t => t.id === editing.id ? { ...t, ...editing } : t));
      setEditing(null); toast.success('Saved');
    } catch(e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Generate panel */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiZap color="#f59e0b" /> Generate Templates with AI
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
          Paste your sample message. OpenAI will generate variations with the same meaning — all links, URLs, and placeholders (like {'{{firstName}}'}) kept exactly as-is.
        </p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Sample Template *</label>
          <textarea value={sample} onChange={e => setSample(e.target.value)}
            placeholder={'Hi {{firstName}},\n\nI wanted to reach out about your Medicare options...\n\nhttps://yourlink.com'}
            rows={6}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Number of Variations</label>
            <input type="number" min={1} max={50} value={count} onChange={e => setCount(Number(e.target.value))}
              style={{ width: 80, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Label (optional)</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Medicare Follow-up"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <button onClick={generate} disabled={generating || !sample.trim()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', background: generating || !sample.trim() ? '#9ca3af' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: generating || !sample.trim() ? 'default' : 'pointer' }}>
            <FiZap size={15} />{generating ? 'Generating…' : `Generate ${count} Templates`}
          </button>
        </div>
      </div>

      {/* Templates list */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>
          Saved Templates <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 14 }}>({templates.length})</span>
        </h3>
        <button onClick={load} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, background: '#f9fafb', cursor: 'pointer' }}>
          <FiRefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
        </button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading…</div>
      : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', background: '#f9fafb', borderRadius: 10, border: '1px dashed #e5e7eb' }}>
          No templates yet — generate some above
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {templates.map((t, i) => (
            <div key={t.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>#{i + 1}</span>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827', flex: 1 }}>{t.name}</span>
                <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}</span>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => copy(t.content)} title="Copy" style={iconBtn('#f0fdf4', '#15803d')}><FiCopy size={13} /></button>
                  <button onClick={() => setEditing({ id: t.id, name: t.name, content: t.content })} title="Edit" style={iconBtn('#eff6ff', '#1a56db')}><FiEdit2 size={13} /></button>
                  <button onClick={() => deleteTemplate(t.id)} title="Delete" style={iconBtn('#fee2e2', '#dc2626')}><FiTrash2 size={13} /></button>
                </div>
              </div>
              <div onClick={() => setExpanded(p => ({ ...p, [t.id]: !p[t.id] }))} style={{ cursor: 'pointer', position: 'relative' }}>
                <div style={{ background: '#f9fafb', borderRadius: 7, padding: '10px 14px', fontSize: 13, color: '#374151', lineHeight: 1.65, whiteSpace: 'pre-wrap', maxHeight: expanded[t.id] ? 'none' : 80, overflow: 'hidden', fontFamily: 'inherit' }}>
                  {t.content}
                </div>
                {!expanded[t.id] && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 30, background: 'linear-gradient(transparent, #f9fafb)', borderRadius: '0 0 7px 7px' }} />}
                <span style={{ fontSize: 11, color: '#6b7280', marginTop: 4, display: 'inline-block' }}>{expanded[t.id] ? '▲ collapse' : '▼ expand'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>Edit Template</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Name</label>
              <input value={editing.name} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Content</label>
              <textarea value={editing.content} onChange={e => setEditing(p => ({ ...p, content: e.target.value }))} rows={10}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, cursor: 'pointer', background: '#fff' }}>Cancel</button>
              <button onClick={saveEdit} disabled={saving}
                style={{ padding: '8px 20px', background: saving ? '#9ca3af' : '#1a56db', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
